import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { runInAction } from "mobx";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton";
import BriaInput from "../../../../../components/common/BriaInput/BriaInput";
import ConfirmationPopup from "../../../../../components/common/ConfirmationPopup/ConfirmationPopup";
import { useAppStore } from "../../../../../hooks/useStores";
import { showErrorToast, showSuccessToast } from "../../../../../utils/toast";
import { TgModel } from "../../../Models/store/tg-model-store";
import styles from "./EvolveModelPopup.module.scss";

type EvolveModelPopupProps = {
	open: boolean;
	onClose: () => void;
	model: TgModel;
};

const EvolveModelPopup = ({ open, onClose, model }: EvolveModelPopupProps) => {
	const modelEvolutionToastId: string = "modelEvolutionToastId";
	const { tgStore, authStore } = useAppStore();
	const { tgProjectStore, tgModelStore } = tgStore;
	const { t } = useTranslation("tg", { keyPrefix: "models.popups.evolve" });

	const [loadingSuggestion, setLoadingSuggestion] = useState(false);
	const [newModelName, setNewModelName] = useState(model.name);
	const [newModelDescription, setNewModelDescription] = useState(
		t("form.description.defaultValue", {
			modelName: model.name,
			feedback_count: model.feedback_count.positive,
			date: new Date().toLocaleDateString(),
		}),
	);

	useEffect(() => {
		open && suggestNewModelName();
	}, [open]);

	const suggestNewModelName = async () => {
		setLoadingSuggestion(true);
		setNewModelName(await tgProjectStore.suggestNewModelName(model.project_id, model.id));
		setNewModelDescription(
			t("form.description.defaultValue", {
				modelName: model.name,
				feedback_count: model.feedback_count.positive,
				date: new Date().toLocaleDateString(),
			}),
		);
		setLoadingSuggestion(false);
	};

	const addTempModel = (): number => {
		// Create optimistic model with temporary ID (max existing ID + 1)
		const maxId = Math.max(...tgProjectStore.paginatedModels.items.map((m) => m.id), 0);
		const optimisticModel: TgModel = {
			...model,
			id: maxId + 1,
			name: newModelName,
			description: newModelDescription,
			status: "InProgress",
			created_at: new Date().toISOString(),
			updated_at: new Date().toISOString(),
			feedback_count: { positive: 0, negative: 0 },
			custom_metadata: JSON.stringify({
				user_created_the_model: {
					name: authStore.user?.userName ?? "",
					email: authStore.user?.email ?? "",
					id: authStore.user?.uid ?? "",
				},
				isOptimistic: true,
			}),
		};

		// Add optimistic model to the list immediately at index 0
		const optimisticIndex = 0;
		tgProjectStore.paginatedModels.items = [optimisticModel, ...tgProjectStore.paginatedModels.items];
		tgProjectStore.paginatedModels.total += 1;
		return optimisticIndex;
	};

	const updateModelRow = (model: TgModel, optimisticIndex: number) => {
		runInAction(() => {
			// Replace optimistic model with real model from API response
			const items = tgProjectStore.paginatedModels.items;
			// Add default feedback_count if API doesn't return it (newly evolved models start with 0)
			const modelWithDefaults: TgModel = {
				...model,
				feedback_count: model.feedback_count ?? { positive: 0, negative: 0 },
			};
			tgProjectStore.paginatedModels.items = [modelWithDefaults, ...items.slice(optimisticIndex + 1)];
		});
	};

	const removeTempModel = (optimisticIndex: number) => {
		// Remove optimistic model on failure
		runInAction(() => {
			const items = tgProjectStore.paginatedModels.items;
			tgProjectStore.paginatedModels.items = items.slice(optimisticIndex + 1);
			tgProjectStore.paginatedModels.total -= 1;
		});
	};

	const handleEvolve = async () => {
		onClose();
		showSuccessToast(t("successMessage", { modelName: newModelName }), modelEvolutionToastId, t("successHeader"));
		const optimisticIndex = addTempModel();

		try {
			const response = await tgModelStore.evolveModel(model.id, newModelName, newModelDescription);
			updateModelRow(response.model, optimisticIndex);
		} catch (error: any) {
			removeTempModel(optimisticIndex);
			showErrorToast(error.message ?? t("errorMessage"), modelEvolutionToastId, t("errorHeader"));
		}
	};

	return (
		<ConfirmationPopup
			open={open}
			title={t("title")}
			onClose={onClose}
			confirmButtonText={t("confirmButton")}
			onClick={handleEvolve}
			disableConfirm={loadingSuggestion || !newModelName.trim()}
		>
			<Box className={styles.content}>
				<Typography className={styles.description}>
					<Trans t={t} i18nKey="content" values={{ modelName: model.name }} />
				</Typography>
				<Box className={styles.nameInputContainer}>
					<BriaInput
						placeholder={t("form.name.placeholder")}
						value={newModelName}
						onChange={(e) => setNewModelName(e.target.value)}
					/>

					<BriaButton loading={loadingSuggestion} disabled={loadingSuggestion} onClick={suggestNewModelName}>
						{t("form.name.suggestButton")}
					</BriaButton>
				</Box>
				<BriaInput
					placeholder={t("form.description.placeholder")}
					value={newModelDescription}
					onChange={(e) => setNewModelDescription(e.target.value)}
				/>
			</Box>
		</ConfirmationPopup>
	);
};

export default observer(EvolveModelPopup);
